/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { AppShell, Box, Group, NavLink, Stack, Text, useMantineTheme } from '@mantine/core';
import { RiArrowRightSLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Paths from '@fai/friendli-suite/features/routes/paths';
import { useTeamName } from '@fai/friendli-suite/features/settings/team-settings/use-team';
import type { Teams } from '@fai/friendli-suite/views/layout/product/team-combobox/drop-down/model';
import { teamOwnerSettingsMenus } from '@fai/friendli-suite/views/layout/product/team-owner-settings-menus';
import TeamAvatar from '@fai/ui/components/avatar/team';
import { TextVariants } from '@fai/ui/components/typography/variants';
import type { PropsWithChildren } from '@fai/util/types';

import { useProductList } from './use-product-list';

function CurrentTeamProfileSection({ teamId }: { teamId: string }) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const { teamName } = useTeamName(teamId);

  return (
    <AppShell.Section component={Stack} gap={0} mt={theme.pf.layout['3xs']}>
      <Box px={theme.pf.layout.sm} py={theme.pf.layout['3xs']}>
        <Text variant={TextVariants.TitleSm}>{t('layout.team')}</Text>
      </Box>
      <Group
        gap={theme.pf.layout['3xs']}
        px={theme.pf.layout.sm}
        py={theme.pf.layout['2xs']}
        wrap="nowrap"
      >
        <TeamAvatar size={20} />
        <Text c={theme.pf.color.fg.neutral.strong} truncate={true} variant={TextVariants.BodyMd}>
          {teamName ?? '-'}
        </Text>
      </Group>
    </AppShell.Section>
  );
}

function TeamMenuSection({ teamId }: { teamId: string }) {
  const theme = useMantineTheme();

  const teamSettingsList = teamOwnerSettingsMenus(teamId);
  const filteredTeamSettingsList = teamSettingsList.filter(({ isVisible }) => isVisible === true);

  if (filteredTeamSettingsList.length <= 0) return null;

  return (
    <AppShell.Section component={Stack} gap={0}>
      {filteredTeamSettingsList.map(({ value, label, leftSection, href }) => (
        <NavLink
          key={value}
          href={href}
          label={label}
          leftSection={leftSection}
          px={theme.pf.layout.sm}
          py={theme.pf.layout['2xs']}
        />
      ))}
    </AppShell.Section>
  );
}

function AllTeamsLink() {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  return (
    <AppShell.Section component={Stack} gap={0}>
      <NavLink
        href={Paths.userSettings.teams}
        label={t('layout.all_teams')}
        px={theme.pf.layout.sm}
        py={theme.pf.layout['2xs']}
        rightSection={<RiArrowRightSLine size={16} />}
      />
    </AppShell.Section>
  );
}

function SwitchableTeamsSection({ teams }: { teams: Teams[] }) {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleTeamSwitchClick = (targetTeamId: string) => {
    navigate(Paths.team(targetTeamId).default);
  };

  return (
    <AppShell.Section component={Stack} gap={0}>
      <Box px={theme.pf.layout.sm} py={theme.pf.layout['3xs']}>
        <Text variant={TextVariants.TitleSm}>{t('layout.switch_team')}</Text>
      </Box>
      {teams?.map(({ name, id }) => (
        <NavLink
          key={id}
          label={name}
          leftSection={<TeamAvatar size={20} />}
          px={theme.pf.layout.sm}
          py={theme.pf.layout['2xs']}
          onClick={() => handleTeamSwitchClick(id)}
        />
      ))}
    </AppShell.Section>
  );
}

function ProductsSection({ teamId }: { teamId: string }) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const productList = useProductList({ teamId });

  if (productList.length <= 0) return null;

  return (
    <AppShell.Section component={Stack} gap={0}>
      <Box px={theme.pf.layout.sm} py={theme.pf.layout['3xs']}>
        <Text variant={TextVariants.TitleSm}>{t('layout.products')}</Text>
      </Box>
      {productList.map(({ value, leftSection, rightSection, label, href }) => (
        <NavLink
          key={value}
          href={href}
          label={label}
          leftSection={leftSection}
          px={theme.pf.layout.sm}
          py={theme.pf.layout['2xs']}
          rightSection={rightSection}
        />
      ))}
    </AppShell.Section>
  );
}

function AllProductsLink({ teamId }: { teamId: string }) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  return (
    <AppShell.Section component={Stack} gap={0}>
      <NavLink
        href={Paths.team(teamId).suite}
        label={
          <Group gap={theme.pf.layout['3xs']}>
            <Text variant={TextVariants.BodyMd}>{t('layout.all_friendli_products')}</Text>
            <RiArrowRightSLine size={16} />
          </Group>
        }
        px={theme.pf.layout.sm}
        py={theme.pf.layout['2xs']}
      />
    </AppShell.Section>
  );
}

function ProductNavbarContainer({ children }: PropsWithChildren) {
  return children;
}

ProductNavbarContainer.displayName = 'ProductNavbarContainer';

ProductNavbarContainer.CurrentTeamProfileSection = CurrentTeamProfileSection;
ProductNavbarContainer.TeamMenuSection = TeamMenuSection;
ProductNavbarContainer.SwitchableTeamsSection = SwitchableTeamsSection;
ProductNavbarContainer.AllTeamsLink = AllTeamsLink;
ProductNavbarContainer.ProductsSection = ProductsSection;
ProductNavbarContainer.AllProductsLink = AllProductsLink;

export default ProductNavbarContainer;
